/**
 * FavoritesManager - Manages favorites dropdown and modals
 *
 * Handles:
 * - Favorites dropdown display
 * - Add Favorite modal with search
 * - Manage Favorites modal
 * - sessionStorage persistence
 */

import type { FavoritePage } from '@/types';
import type { Route } from '@/types';
import { StorageService } from '@/services/StorageService';
import { createElement } from '@/ui/DOMHelpers';

export class FavoritesManager {
	private storageService: StorageService;
	private routes: Route[];
	private dropdownElement: HTMLElement | null = null;
	private isDropdownOpen = false;

	/**
	 * Constructor
	 */
	constructor(storageService: StorageService, routes: Route[]) {
		this.storageService = storageService;
		this.routes = routes;
	}

	/**
	 * Initialize the favorites functionality
	 */
	initialize(): void {
		this.setupDropdownToggle();
		this.renderDropdown();
		this.setupClickOutside();
	}

	/**
	 * Setup dropdown toggle functionality
	 */
	private setupDropdownToggle(): void {
		const favoritesLink = document.getElementById('favorites-link');
		if (!favoritesLink) return;

		favoritesLink.addEventListener('click', (e) => {
			e.preventDefault();
			e.stopPropagation();
			this.toggleDropdown();
		});
	}

	/**
	 * Toggle dropdown visibility
	 */
	private toggleDropdown(): void {
		this.isDropdownOpen = !this.isDropdownOpen;
		if (this.isDropdownOpen) {
			this.showDropdown();
		} else {
			this.hideDropdown();
		}
	}

	/**
	 * Show the dropdown
	 */
	private showDropdown(): void {
		this.renderDropdown();
		if (this.dropdownElement) {
			this.dropdownElement.style.display = 'block';
		}
	}

	/**
	 * Hide the dropdown
	 */
	private hideDropdown(): void {
		if (this.dropdownElement) {
			this.dropdownElement.style.display = 'none';
		}
		this.isDropdownOpen = false;
	}

	/**
	 * Render the dropdown content
	 */
	private renderDropdown(): void {
		// Find or create dropdown element
		this.dropdownElement = document.getElementById('favorites-dropdown');
		if (!this.dropdownElement) {
			this.dropdownElement = createElement('div');
			this.dropdownElement.id = 'favorites-dropdown';
			this.dropdownElement.className = 'favorites-dropdown';

			// Insert after favorites link
			const favoritesLink = document.getElementById('favorites-link');
			if (favoritesLink && favoritesLink.parentElement) {
				// Create a wrapper for positioning
				const wrapper = createElement('div');
				wrapper.style.position = 'relative';
				wrapper.style.display = 'inline-block';

				// Move favorites link into wrapper
				favoritesLink.parentNode?.insertBefore(wrapper, favoritesLink);
				wrapper.appendChild(favoritesLink);
				wrapper.appendChild(this.dropdownElement);
			}
		}

		// Clear and rebuild dropdown content
		this.dropdownElement.innerHTML = '';

		// Add "Add as Favorite..." option
		const addOption = this.createDropdownOption('Add as Favorite...');
		addOption.addEventListener('click', (e) => {
			e.stopPropagation();
			this.hideDropdown();
			this.showAddFavoriteModal();
		});
		this.dropdownElement.appendChild(addOption);

		// Add "Manage Favorites..." option
		const manageOption = this.createDropdownOption('Manage Favorites...');
		manageOption.addEventListener('click', (e) => {
			e.stopPropagation();
			this.hideDropdown();
			this.showManageFavoritesModal();
		});
		this.dropdownElement.appendChild(manageOption);

		// Add divider
		const divider = createElement('div');
		divider.className = 'favorites-divider';
		this.dropdownElement.appendChild(divider);

		// Add favorite pages
		const favorites = this.storageService.getFavorites();
		if (favorites.length > 0) {
			favorites.forEach((favorite) => {
				const favoriteOption = this.createDropdownOption(favorite.title);
				favoriteOption.addEventListener('click', (e) => {
					e.stopPropagation();
					this.hideDropdown();
					window.location.href = favorite.path;
				});
				this.dropdownElement!.appendChild(favoriteOption);
			});
		} else {
			const emptyMessage = createElement('div');
			emptyMessage.className = 'favorites-empty-message';
			emptyMessage.textContent = 'No favorites yet';
			this.dropdownElement.appendChild(emptyMessage);
		}
	}

	/**
	 * Create a dropdown option element
	 */
	private createDropdownOption(text: string): HTMLElement {
		const option = createElement('div');
		option.className = 'favorites-dropdown-option';
		option.textContent = text;
		return option;
	}

	/**
	 * Setup click outside to close dropdown
	 */
	private setupClickOutside(): void {
		document.addEventListener('click', (e) => {
			const target = e.target as HTMLElement;
			const favoritesLink = document.getElementById('favorites-link');

			if (
				this.isDropdownOpen &&
				!this.dropdownElement?.contains(target) &&
				!favoritesLink?.contains(target)
			) {
				this.hideDropdown();
			}
		});
	}

	/**
	 * Show the Add Favorite modal
	 */
	private showAddFavoriteModal(): void {
		const overlay = this.createModalOverlay();
		const modal = this.createAddFavoriteModal();

		overlay.appendChild(modal);
		document.body.appendChild(overlay);

		// Setup search functionality
		const searchInput = modal.querySelector('.favorites-search-bar') as HTMLInputElement;
		const pagesList = modal.querySelector('.favorites-pages-list') as HTMLElement;

		if (searchInput && pagesList) {
			searchInput.addEventListener('input', () => {
				this.filterPages(searchInput.value, pagesList);
			});

			// Focus search input
			searchInput.focus();
		}

		// Setup Save button
		const saveButton = modal.querySelector('.favorites-modal-save') as HTMLButtonElement;
		if (saveButton) {
			saveButton.addEventListener('click', () => {
				this.saveSelectedFavorites(modal);
				overlay.remove();
			});
		}

		// Setup Cancel button
		const cancelButton = modal.querySelector('.favorites-modal-cancel') as HTMLButtonElement;
		if (cancelButton) {
			cancelButton.addEventListener('click', () => {
				overlay.remove();
			});
		}

		// Close on overlay click
		overlay.addEventListener('click', (e) => {
			if (e.target === overlay) {
				overlay.remove();
			}
		});

		// Close on Escape key
		const escapeHandler = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				overlay.remove();
				document.removeEventListener('keydown', escapeHandler);
			}
		};
		document.addEventListener('keydown', escapeHandler);
	}

	/**
	 * Show the Manage Favorites modal
	 */
	private showManageFavoritesModal(): void {
		const overlay = this.createModalOverlay();
		const modal = this.createManageFavoritesModal();

		overlay.appendChild(modal);
		document.body.appendChild(overlay);

		// Setup delete functionality
		this.setupDeleteHandlers(modal);

		// Setup Close button
		const closeButton = modal.querySelector('.favorites-modal-close') as HTMLButtonElement;
		if (closeButton) {
			closeButton.addEventListener('click', () => {
				overlay.remove();
			});
		}

		// Close on overlay click
		overlay.addEventListener('click', (e) => {
			if (e.target === overlay) {
				overlay.remove();
			}
		});

		// Close on Escape key
		const escapeHandler = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				overlay.remove();
				document.removeEventListener('keydown', escapeHandler);
			}
		};
		document.addEventListener('keydown', escapeHandler);
	}

	/**
	 * Create modal overlay element
	 */
	private createModalOverlay(): HTMLElement {
		const overlay = createElement('div');
		overlay.className = 'favorites-modal-overlay';
		return overlay;
	}

	/**
	 * Create Add Favorite modal
	 */
	private createAddFavoriteModal(): HTMLElement {
		const modal = createElement('div');
		modal.className = 'favorites-modal';

		// Title
		const title = createElement('h3');
		title.className = 'favorites-modal-title';
		title.textContent = 'Add as Favorite';
		modal.appendChild(title);

		// Search bar
		const searchBar = createElement('input');
		searchBar.type = 'text';
		searchBar.className = 'favorites-search-bar';
		searchBar.placeholder = 'Search pages...';
		modal.appendChild(searchBar);

		// Pages list
		const pagesList = createElement('div');
		pagesList.className = 'favorites-pages-list';

		// Get available pages (exclude home page)
		const availableRoutes = this.routes.filter((route) => route.path !== '/');
		const favorites = this.storageService.getFavorites();
		const favoritesPaths = favorites.map((f) => f.path);

		availableRoutes.forEach((route) => {
			const pageItem = createElement('div');
			pageItem.className = 'favorites-page-item';
			pageItem.setAttribute('data-path', route.path);
			pageItem.setAttribute('data-title', route.sidebarItem || route.title);
			pageItem.style.cursor = 'pointer';

			const checkbox = createElement('input');
			checkbox.type = 'checkbox';
			checkbox.className = 'favorites-checkbox';
			checkbox.checked = favoritesPaths.includes(route.path);

			const label = createElement('label');
			label.textContent = route.sidebarItem || route.title;
			label.style.marginLeft = '8px';
			label.style.cursor = 'pointer';

			// Click on entire row to toggle checkbox
			pageItem.addEventListener('click', (e) => {
				// Don't double-toggle if clicking directly on checkbox
				if (e.target !== checkbox) {
					checkbox.checked = !checkbox.checked;
				}
			});

			pageItem.appendChild(checkbox);
			pageItem.appendChild(label);
			pagesList.appendChild(pageItem);
		});

		modal.appendChild(pagesList);

		// Button container
		const buttonContainer = createElement('div');
		buttonContainer.className = 'favorites-modal-buttons';

		const saveButton = createElement('button');
		saveButton.className = 'favorites-modal-button favorites-modal-save';
		saveButton.textContent = 'Save';

		const cancelButton = createElement('button');
		cancelButton.className = 'favorites-modal-button favorites-modal-cancel';
		cancelButton.textContent = 'Cancel';

		buttonContainer.appendChild(saveButton);
		buttonContainer.appendChild(cancelButton);
		modal.appendChild(buttonContainer);

		return modal;
	}

	/**
	 * Create Manage Favorites modal
	 */
	private createManageFavoritesModal(): HTMLElement {
		const modal = createElement('div');
		modal.className = 'favorites-modal';

		// Title
		const title = createElement('h3');
		title.className = 'favorites-modal-title';
		title.textContent = 'Manage Favorites';
		modal.appendChild(title);

		// Favorites list
		const favoritesList = createElement('div');
		favoritesList.className = 'favorites-manage-list';

		const favorites = this.storageService.getFavorites();
		if (favorites.length === 0) {
			const emptyMessage = createElement('p');
			emptyMessage.textContent = 'No favorites yet';
			emptyMessage.style.textAlign = 'center';
			emptyMessage.style.color = '#666';
			favoritesList.appendChild(emptyMessage);
		} else {
			favorites.forEach((favorite, index) => {
				const favoriteItem = createElement('div');
				favoriteItem.className = 'favorites-manage-item';
				favoriteItem.setAttribute('data-index', String(index));

				const favoriteName = createElement('span');
				favoriteName.textContent = favorite.title;
				favoriteName.style.flex = '1';

				const deleteButton = createElement('button');
				deleteButton.className = 'favorites-delete-button';
				deleteButton.textContent = 'Delete';

				favoriteItem.appendChild(favoriteName);
				favoriteItem.appendChild(deleteButton);
				favoritesList.appendChild(favoriteItem);
			});
		}

		modal.appendChild(favoritesList);

		// Close button
		const buttonContainer = createElement('div');
		buttonContainer.className = 'favorites-modal-buttons';

		const closeButton = createElement('button');
		closeButton.className = 'favorites-modal-button favorites-modal-close';
		closeButton.textContent = 'Close';

		buttonContainer.appendChild(closeButton);
		modal.appendChild(buttonContainer);

		return modal;
	}

	/**
	 * Filter pages based on search query
	 */
	private filterPages(query: string, pagesList: HTMLElement): void {
		const items = pagesList.querySelectorAll('.favorites-page-item');
		const lowerQuery = query.toLowerCase();

		items.forEach((item) => {
			const title = item.getAttribute('data-title')?.toLowerCase() || '';
			if (title.includes(lowerQuery)) {
				(item as HTMLElement).style.display = 'flex';
			} else {
				(item as HTMLElement).style.display = 'none';
			}
		});
	}

	/**
	 * Save selected favorites
	 */
	private saveSelectedFavorites(modal: HTMLElement): void {
		const checkboxes = modal.querySelectorAll('.favorites-checkbox');
		const newFavorites: FavoritePage[] = [];

		checkboxes.forEach((checkbox) => {
			if (checkbox.checked) {
				const pageItem = checkbox.closest('.favorites-page-item');
				if (pageItem) {
					const path = pageItem.getAttribute('data-path');
					const title = pageItem.getAttribute('data-title');
					if (path && title) {
						newFavorites.push({ path, title });
					}
				}
			}
		});

		this.storageService.setFavorites(newFavorites);
		this.renderDropdown(); // Refresh dropdown
	}

	/**
	 * Setup delete handlers for manage modal
	 */
	private setupDeleteHandlers(modal: HTMLElement): void {
		const deleteButtons = modal.querySelectorAll('.favorites-delete-button');

		deleteButtons.forEach((button) => {
			button.addEventListener('click', (e) => {
				const favoriteItem = (e.target as HTMLElement).closest('.favorites-manage-item');
				if (favoriteItem) {
					const index = parseInt(favoriteItem.getAttribute('data-index') || '-1', 10);
					if (index >= 0) {
						const favorites = this.storageService.getFavorites();
						favorites.splice(index, 1);
						this.storageService.setFavorites(favorites);

						// Remove the item from DOM
						favoriteItem.remove();

						// If no favorites left, show empty message
						const favoritesList = modal.querySelector('.favorites-manage-list');
						if (favoritesList && favorites.length === 0) {
							favoritesList.innerHTML = '';
							const emptyMessage = createElement('p');
							emptyMessage.textContent = 'No favorites yet';
							emptyMessage.style.textAlign = 'center';
							emptyMessage.style.color = '#666';
							favoritesList.appendChild(emptyMessage);
						}

						// Refresh dropdown
						this.renderDropdown();
					}
				}
			});
		});
	}
}
